import React, { useMemo } from 'react'
import { Box, Text, Icon } from '@deseretbook/react-ui'
import { Link } from 'react-router-dom'

import { listenAudiobookUrl, readEbookUrl } from '../../utils/routes'
import { truncateText } from '../../utils/truncateText'
import { openPopupPlayer } from '../../utils/media'

import './ContinueMedia.css'
import { useMediaQuery } from '@deseretbook/react-utils'

/**
 * Safely pull the most‑recent items from localStorage and return the first three.
 */
const getContinueMedia = () => {
  try {
    const stored = localStorage.getItem('continueMedia')
    return stored ? JSON.parse(stored).slice(0, 3) : []
  } catch {
    // If parsing fails, treat it as empty – we don’t want to break rendering.
    return []
  }
}

/**
 * Single media tile (ebook / audiobook).
 */
const MediaCard = ({ media }) => {
  const { continueVariant: variant } = media

  // Prefer variant‑specific thumbnail, fall back to generic cover.
  const thumbnail =
    (media[variant] && media[variant].cover && media[variant].cover.thumbUrl) ||
    (media.cover && media.cover.thumbnailUrl) ||
    ''

  // Build the correct click behavior based on variant.
  const buttonProps =
    variant === 'audiobook'
      ? {
          as: 'button',
          type: 'button',
          onClick: () =>
            openPopupPlayer(
              listenAudiobookUrl(media.mediaId, media.audiobook && media.audiobook.sku),
            ),
        }
      : {
          as: Link,
          to: readEbookUrl(media.mediaId, media.ebook && media.ebook.sku),
        }

  const authors = media.authors
    .map(({ firstName, lastName }) => `${firstName} ${lastName}`)
    .join(', ')

  return (
    <Box
      className="continue-container-card"
      alignItems="center"
      justifyContent="center"
      isFullwidth
      {...buttonProps}
    >
      <figure className="continue-image">
        <img src={thumbnail} alt={media.title} />
      </figure>

      <Box isColumn alignItems="flex-start">
        <Text className="continue-title" title={media.title}>
          {truncateText(media.title, 45)}
        </Text>
        <Text className="continue-author">{authors}</Text>
      </Box>
    </Box>
  )
}

/**
 * Main component – renders a small carousel of the user’s in‑progress media.
 */
const ContinueMedia = () => {
  const mediaItems = useMemo(getContinueMedia, [])
  const isDesktopOrLarger = useMediaQuery(1405)
  const isTabletOrSmaller = useMediaQuery({ maxWidth: 800 })

  if (!mediaItems.length) return null

  let justifyContent = 'space-evenly'

  if (isDesktopOrLarger || (!isTabletOrSmaller && mediaItems.length === 1)) {
    justifyContent = 'flex-start'
  }

  return (
    <Box id="ContinueContainer">
      <Text
        style={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '12px',
          display: 'inline-block',
        }}
        className="continue-heading"
      >
        Continue
      </Text>

      <Box className="carousel-track" alignItems="center" justifyContent={justifyContent} isWrapped>
        {mediaItems.map((media) => (
          <MediaCard key={media.mediaId} media={media} />
        ))}

        {/* Fallback tile – push users to their full library. */}
        <Box
          as={Link}
          to="/library"
          className="continue-container-card continue-container-card-library"
          alignItems="center"
          justifyContent="center"
        >
          <Icon name="library" className="continue-library-icon" />
          <Text className="continue-title">Your Library</Text>
        </Box>
      </Box>
    </Box>
  )
}

export default ContinueMedia
